import { useState } from 'react';
import { View, TextInput, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from '@/components/ui/Text';

type TripSearchBarProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  historyShortcutVisible?: boolean;
  onHistoryShortcutPress?: () => void;
  historyLabel?: string;
  tone?: 'default' | 'inverse';
};

export function TripSearchBar({
  value,
  onChangeText,
  placeholder = 'Search trips...',
  historyShortcutVisible = true,
  onHistoryShortcutPress,
  historyLabel = 'History',
  tone = 'default',
}: TripSearchBarProps) {
  const theme = useTheme();
  const showHistoryShortcut = historyShortcutVisible && typeof onHistoryShortcutPress === 'function';
  const isInverse = tone === 'inverse';
  const chromeBg = isInverse ? 'rgba(255,255,255,0.08)' : theme.colors.backgroundSecondary;
  const chromeBorder = isInverse ? 'rgba(255,255,255,0.18)' : theme.colors.borderMuted;
  const contentColor = isInverse ? theme.colors.surface : theme.colors.text;
  const mutedColor = isInverse ? 'rgba(255,255,255,0.75)' : theme.colors.textSecondary;
  
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: chromeBg,
        borderRadius: theme.radii.lg,
        paddingHorizontal: theme.spacing(1.25),
        paddingVertical: theme.spacing(0.5),
        borderWidth: 1,
        borderColor: chromeBorder,
        gap: theme.spacing(0.75),
      }}
    >
      <Ionicons name="search-outline" size={20} color={mutedColor} />
      <TextInput
        id="trip-search-input"
        name="trip-search-input"
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={mutedColor}
        style={{
          flex: 1,
          fontSize: 16,
          color: contentColor,
          paddingVertical: theme.spacing(0.5),
          minHeight: 46, // WCAG minimum touch target
        }}
        accessibilityLabel="Search trips"
        accessibilityHint="Type to search your trips by name, destination, or date"
        accessibilityRole="search"
      />
      {value.length > 0 && (
        <Pressable
          onPress={() => onChangeText('')}
          style={{
            padding: theme.spacing(0.5),
            minHeight: 44, // WCAG minimum touch target
            minWidth: 44,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
          accessibilityHint="Clears the search input"
        >
          <Ionicons name="close-circle" size={20} color={mutedColor} />
        </Pressable>
      )}
      {showHistoryShortcut && (
        <Pressable
          onPress={onHistoryShortcutPress}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.25),
            backgroundColor: isInverse ? 'rgba(255,255,255,0.14)' : theme.colors.primary + '10',
            paddingHorizontal: theme.spacing(1),
            paddingVertical: theme.spacing(0.5),
            borderRadius: theme.radii.pill,
            opacity: pressed ? 0.7 : 1,
            minHeight: 44, // WCAG minimum touch target
          })}
          accessibilityRole="button"
          accessibilityLabel={`Open ${historyLabel.toLowerCase()}`}
          accessibilityHint="Opens your completed trip history"
        >
          <Ionicons name="time-outline" size={14} color={isInverse ? theme.colors.surface : theme.colors.primary} />
          <ThemedText
            variant="label"
            style={{
              fontSize: 12,
              color: isInverse ? theme.colors.surface : theme.colors.primary,
              fontWeight: '600',
            }}
          >
            {historyLabel}
          </ThemedText>
        </Pressable>
      )}
    </View>
  );
}

